import { ColumnVisibilityPicker } from "./ColumnVisibilityPicker";
import { DataTableActions } from "./DataTableActions";
import { DataTableActiveFilters } from "./DataTableActiveFilters";
import { DataTableFilters } from "./DataTableFilters";
import { DataTableRoot } from "./DataTableRoot";
import { DataTableSelection } from "./DataTableSelection";
import { DataTableTable } from "./DataTableTable";

/**
 * Compound primitive for rendering a paginated, sortable, selectable data
 * table. Composed of:
 *
 * - `DataTable.Root` — outer flex column that owns layout and spacing, hosts
 *   the table, and lets sibling chrome (filters, alerts) sit alongside.
 * - `DataTable.Actions` — horizontal row above the table for action buttons
 *   (filters, columns, export). Wraps to a second line on narrow viewports.
 * - `DataTable.Table` — the bordered table surface itself, including its
 *   pagination footer. Consumes the table slice from `useDataTableState`
 *   and the columns built via `createColumnHelper`.
 * - `DataTable.Filters` — filter button + popover that emits a structured
 *   `Filter[]` for the consumer to convert into a wire `Condition`.
 * - `DataTable.ColumnVisibility` — "Columns" button + popover that toggles
 *   which columns are visible. Picker is purely presentational; the
 *   consumer owns the visible-id set and `onToggle(id)` flows individual
 *   toggles back.
 * - `DataTable.Selection` — "Selection" button + menu for bulk actions
 *   on the current selection. Lives in the `Actions` row alongside
 *   `Filters` and `ColumnVisibility`. Spread `tableState.selectionSlice`
 *   onto it and pass an `options` array; the primitive handles the
 *   button state, the menu, and clearing selection after each action.
 * - `DataTable.ActiveFilters` — chip row that visualizes the applied
 *   filters. Renders nothing when no filters are applied. Supports
 *   removing individual filters (`×`), clearing all (`Clear`), toggling
 *   AND/OR match mode, and adding a new filter inline (`+`).
 *
 * State and column definitions are produced outside the tree:
 * - `useDataTableState` returns a `.table` slice that spreads directly onto
 *   `DataTable.Table`.
 * - `createColumnHelper<TData>()` builds typed column definitions.
 *
 * @example
 * ```tsx
 * const columnHelper = createColumnHelper<Row>();
 * const columns = [columnHelper.accessor("name", { headerName: "Name" })];
 *
 * function MyTable({ rows }: { rows: Row[] }) {
 *   const tableState = useDataTableState({ initialSort });
 *   return (
 *     <DataTable.Root>
 *       <DataTable.Table<Row>
 *         {...tableState.table}
 *         data={rows}
 *         columns={columns}
 *         getRowId={(r) => r.id}
 *         totalRowCount={countQuery.data}
 *         noRowsContent="No rows"
 *       />
 *     </DataTable.Root>
 *   );
 * }
 * ```
 */
export const DataTable = {
  Root: DataTableRoot,
  Actions: DataTableActions,
  Table: DataTableTable,
  Filters: DataTableFilters,
  ActiveFilters: DataTableActiveFilters,
  ColumnVisibility: ColumnVisibilityPicker,
  Selection: DataTableSelection,
};
